/**
 * Message thread component.
 *
 * Displays a conversation's message thread with partner information, message
 * history, and message input. Handles message sending, auto-scrolling to
 * latest messages, and responsive layout for mobile devices.
 *
 * @module MessageThread
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  AlertCircle,
  ArrowLeft,
  Flag,
  MessageCircle,
  MoreVertical,
  Send,
  ShieldOff,
  ShieldX,
} from "lucide-react";
import { Conversation, Message } from "@/types/partner-finder";
import { cn } from "@/lib/utils";
import { formatTime, getInitials } from "@/lib/mappers";
import { blockUser, getBlockStatus, unblockUser } from "@/lib/moderationApi";
import { ReportUserDialog } from "./ReportUserDialog";

interface MessageThreadProps {
  readonly conversation: Conversation | null;
  readonly messages: Message[];
  readonly onSendMessage: (content: string) => void;
  readonly onBack?: () => void;
  /** When false the partner has no active request; the input is replaced with a notice. */
  readonly canSendMessage?: boolean;
  /** Messages the current user can still send in this session. null = no session limit applies. */
  readonly messagesRemaining?: number | null;
}

/**
 * Component for displaying a message thread with conversation header and message input
 */
export function MessageThread({
  conversation,
  messages,
  onSendMessage,
  onBack,
  canSendMessage = true,
  messagesRemaining = null,
}: MessageThreadProps) {
  const { t } = useTranslation();
  const [messageInput, setMessageInput] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [unblockDialogOpen, setUnblockDialogOpen] = useState(false);
  const [iBlockedThem, setIBlockedThem] = useState(false);
  const [theyBlockedMe, setTheyBlockedMe] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    // Scroll to bottom when messages change
    const scrollContainer = document.querySelector(
      "[data-radix-scroll-area-viewport]",
    );
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    let cancelled = false;
    const partnerId = conversation?.partnerId;
    if (!partnerId) {
      setIBlockedThem(false);
      setTheyBlockedMe(false);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      const status = await getBlockStatus(partnerId);
      if (cancelled || !status) return;
      setIBlockedThem(status.iBlockedThem);
      setTheyBlockedMe(status.theyBlockedMe);
    })();
    return () => {
      cancelled = true;
    };
  }, [conversation?.partnerId]);

  const handleBlockConfirm = async () => {
    if (!conversation?.partnerId) return;
    setActionLoading(true);
    const ok = await blockUser(conversation.partnerId);
    setActionLoading(false);
    setBlockDialogOpen(false);
    if (ok) {
      setIBlockedThem(true);
      toast.success(
        t("moderation.block.success", {
          defaultValue: "User blocked. They can no longer message you.",
        }),
      );
    }
  };

  const handleUnblockConfirm = async () => {
    if (!conversation?.partnerId) return;
    setActionLoading(true);
    const ok = await unblockUser(conversation.partnerId);
    setActionLoading(false);
    setUnblockDialogOpen(false);
    if (ok) {
      setIBlockedThem(false);
      toast.success(
        t("moderation.unblock.success", { defaultValue: "User unblocked." }),
      );
    }
  };

  const messagingDisabledByBlock = iBlockedThem || theyBlockedMe;
  const sessionLimitReached =
    messagesRemaining !== null && messagesRemaining <= 0;
  const effectiveCanSend =
    canSendMessage && !messagingDisabledByBlock && !sessionLimitReached;

  const handleSend = () => {
    if (messageInput.trim()) {
      onSendMessage(messageInput);
      setMessageInput("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-center">
        <div className="space-y-4">
          <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground" />
          <div>
            <h3 className="text-lg font-semibold mb-2">
              {t("partnerFinder.messages.selectConversation", {
                defaultValue: "No conversation selected",
              })}
            </h3>
            <p className="text-muted-foreground">
              {t("partnerFinder.messages.selectConversationDesc", {
                defaultValue: "Select a conversation to start messaging",
              })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex shrink-0 items-center gap-2 border-b p-3 sm:gap-3 sm:p-4">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="lg:hidden"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarFallback>
            {getInitials(conversation.partnerName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{conversation.partnerName}</div>
          <Badge variant="outline" className="text-xs">
            {conversation.partnerLevel}
          </Badge>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0"
              aria-label={t("moderation.menu.aria", {
                defaultValue: "Conversation actions",
              })}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => setReportOpen(true)}>
              <Flag className="mr-2 h-4 w-4" />
              {t("moderation.menu.report", { defaultValue: "Report user" })}
            </DropdownMenuItem>
            {iBlockedThem ? (
              <DropdownMenuItem
                onClick={() => setUnblockDialogOpen(true)}
                className="text-foreground"
              >
                <ShieldOff className="mr-2 h-4 w-4" />
                {t("moderation.menu.unblock", { defaultValue: "Unblock user" })}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={() => setBlockDialogOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                <ShieldX className="mr-2 h-4 w-4" />
                {t("moderation.menu.block", { defaultValue: "Block user" })}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-3 sm:p-4">
        <div className="space-y-4">
          {messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <p>
                {t("partnerFinder.messages.noMessages", {
                  defaultValue: "No messages yet. Start the conversation!",
                })}
              </p>
            </div>
          ) : (
            messages.map((message) => {
              const isCurrentUser = message.senderId !== conversation.partnerId;
              return (
                <div
                  key={message.id}
                  className={cn(
                    "flex",
                    isCurrentUser ? "justify-end" : "justify-start",
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[85%] rounded-lg px-3 py-2 sm:max-w-[75%] sm:px-4",
                      isCurrentUser
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted",
                      message.isSystem &&
                        "bg-muted/50 text-center w-full max-w-full",
                    )}
                  >
                    {message.isSystem ? (
                      <p className="text-xs text-muted-foreground italic">
                        {message.content}
                      </p>
                    ) : (
                      <>
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {message.content}
                        </p>
                        <p className="text-xs mt-1 opacity-70">
                          {formatTime(message.timestamp)}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Message Input */}
      <div className="safe-area-bottom shrink-0 border-t px-3 pt-3 sm:p-4">
        {effectiveCanSend ? (
          <>
            <div className="flex gap-2">
              <textarea
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t(
                  "partnerFinder.messages.typeMessagePlaceholder",
                  {
                    defaultValue: "Type a message...",
                  },
                )}
                className="min-h-11 max-h-32 flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 lg:text-sm"
                rows={1}
              />
              <Button
                onClick={handleSend}
                size="icon"
                disabled={!messageInput.trim()}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            {messagesRemaining !== null && messagesRemaining > 0 ? (
              <p className="text-xs text-muted-foreground mt-2">
                {t("partnerFinder.messages.messagesRemaining", {
                  count: messagesRemaining,
                  defaultValue_one:
                    "{{count}} message remaining in this session",
                  defaultValue: "{{count}} messages remaining in this session",
                })}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-2">
                {t("partnerFinder.messages.sendHint", {
                  defaultValue: "Press Enter to send, Shift+Enter for new line",
                })}
              </p>
            )}
          </>
        ) : (
          <div className="flex items-center gap-3 rounded-md border border-border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4 shrink-0 text-amber-500" />
            <span>
              {messagingDisabledByBlock
                ? t("moderation.disabled.unavailable", {
                    defaultValue: "Messaging with this user is unavailable.",
                  })
                : sessionLimitReached
                  ? t("partnerFinder.messages.sessionLimitReached", {
                      defaultValue:
                        "You've reached the 10-message limit for this session.",
                    })
                  : conversation?.partnerName
                    ? t("partnerFinder.messages.partnerRequestRemovedNamed", {
                        name: conversation.partnerName,
                        defaultValue: `${conversation.partnerName} has removed their partner request. Sending new messages is disabled.`,
                      })
                    : t("partnerFinder.messages.partnerRequestRemoved", {
                        defaultValue:
                          "This partner has removed their request. Sending new messages is disabled.",
                      })}
            </span>
          </div>
        )}
      </div>

      {conversation?.partnerId && (
        <ReportUserDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          reportedUserId={conversation.partnerId}
          reportedUserName={conversation.partnerName}
        />
      )}

      <AlertDialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("moderation.block.confirmTitle", {
                defaultValue: "Block {{name}}?",
                name: conversation?.partnerName,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("moderation.block.confirmBody", {
                defaultValue:
                  "You will not receive new messages from this user, and they cannot message you. Existing messages stay in your inbox until you delete the conversation. You can unblock them later.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBlockConfirm}
              disabled={actionLoading}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t("moderation.menu.block", { defaultValue: "Block user" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={unblockDialogOpen} onOpenChange={setUnblockDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("moderation.unblock.confirmTitle", {
                defaultValue: "Unblock {{name}}?",
                name: conversation?.partnerName,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("moderation.unblock.confirmBody", {
                defaultValue: "They will be able to message you again.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnblockConfirm}
              disabled={actionLoading}
            >
              {t("moderation.menu.unblock", { defaultValue: "Unblock user" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
